import { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import {
  addressMasterApi,
  getImageUrl,
  userAwardsCertificateApi,
  userMasterApi,
  userSocialMediaProfileApi,
} from "../../api/masterApi";
import { getAdminUser } from "../../utils/auth";
import "./VerifyUserModal.css";

const USER_VERIFY_PREFIXES = [
  {
    prefix: "education",
    label: "Education Verification",
    verified: "Educational Qualification Verified",
  },
  {
    prefix: "criminal_record",
    label: "Criminal Record Verification",
    verified: "Criminal Record Verified",
  },
  {
    prefix: "business_integrity",
    label: "Business Integrity Verification",
    verified: "Business Integrity Verified",
  },
  {
    prefix: "kyc",
    label: "Business / User KYC Verification",
    verified: "KYC Verified",
  },
  {
    prefix: "bank_details",
    label: "Bank Details Verification",
    verified: "Bank Details Verified",
  },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function verifierName() {
  return getAdminUser()?.name || "Verifier";
}

function display(value) {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    const cleaned = value.filter(Boolean);
    return cleaned.length ? cleaned.join(", ") : "—";
  }
  return String(value);
}

function boolLabel(value) {
  return Number(value) === 1 || value === true ? "Yes" : "No";
}

function ReadRow({ label, value }) {
  return (
    <div className="verify-modal__read-row">
      <span className="verify-modal__read-label">{label}</span>
      <span className="verify-modal__read-value">{value}</span>
    </div>
  );
}

function TableReadRow({ label, value }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>{value}</td>
    </tr>
  );
}

function FileLink({ path, label = "View file" }) {
  if (!path) return <span>—</span>;
  return (
    <a href={getImageUrl(path)} target="_blank" rel="noreferrer">
      {label}
    </a>
  );
}

function VerificationEditor({
  verified,
  by,
  date,
  certificate,
  certificateFile,
  labels,
  onChange,
}) {
  const fileLabel = certificateFile
    ? certificateFile.name
    : certificate
      ? String(certificate).split("/").pop()
      : "No file chosen";

  return (
    <div className="verify-modal__verify-fields">
      <label className="verify-modal__field">
        <span>{labels.verified}</span>
        <select
          value={verified ? "1" : "0"}
          onChange={(e) => {
            const next = e.target.value === "1";
            onChange({
              verified: next,
              by: next ? by || verifierName() : by,
              date: next ? date || todayISO() : date,
            });
          }}
        >
          <option value="0">No</option>
          <option value="1">Yes</option>
        </select>
      </label>
      <label className="verify-modal__field">
        <span>{labels.by}</span>
        <input type="text" value={by || ""} readOnly />
      </label>
      <label className="verify-modal__field">
        <span>{labels.date}</span>
        <input
          type="date"
          value={date || ""}
          onChange={(e) => onChange({ date: e.target.value })}
        />
      </label>
      <div className="verify-modal__field">
        <span>{labels.certificate}</span>
        <div className="verify-modal__file-input">
          <label className="verify-modal__file-btn">
            Choose file
            <input
              type="file"
              accept="image/*,.pdf,application/pdf"
              onChange={(e) =>
                onChange({ certificateFile: e.target.files?.[0] || null })
              }
            />
          </label>
          <span className="verify-modal__file-name" title={fileLabel}>
            {certificate && !certificateFile ? (
              <FileLink path={certificate} label={fileLabel} />
            ) : (
              fileLabel
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function mapUserVerification(user = {}) {
  const state = {};
  USER_VERIFY_PREFIXES.forEach(({ prefix }) => {
    state[prefix] = {
      verified: Number(user[`${prefix}_verified`]) === 1,
      by: user[`${prefix}_verification_by`] || "",
      date: user[`${prefix}_verification_date`]
        ? String(user[`${prefix}_verification_date`]).slice(0, 10)
        : "",
      certificate: user[`${prefix}_verification_certificate`] || "",
      certificateFile: null,
    };
  });
  return state;
}

function mapRowVerification(row, keys) {
  return {
    verified: Number(row[keys.verified]) === 1,
    by: row[keys.by] || "",
    date: row[keys.date] ? String(row[keys.date]).slice(0, 10) : "",
    certificate: row[keys.certificate] || "",
    certificateFile: null,
  };
}

export default function VerifyUserModal({ open, loginRow, onClose, onSaved }) {
  const userId = loginRow?.reference_id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [social, setSocial] = useState([]);
  const [awards, setAwards] = useState([]);
  const [userVerify, setUserVerify] = useState({});
  const [addressVerify, setAddressVerify] = useState({});
  const [socialVerify, setSocialVerify] = useState({});
  const [awardsVerify, setAwardsVerify] = useState({});
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });

  const showToast = (message, severity = "error") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    if (!open || !userId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [userData, addressRows, socialRows, awardRows] = await Promise.all([
          userMasterApi.getOne(userId),
          addressMasterApi.list({ user_id: userId }),
          userSocialMediaProfileApi.list({ user_id: userId }),
          userAwardsCertificateApi.list({ user_id: userId }),
        ]);
        if (cancelled) return;

        setUser(userData || null);
        setAddresses(addressRows || []);
        setSocial(socialRows || []);
        setAwards(awardRows || []);
        setUserVerify(mapUserVerification(userData || {}));

        const nextAddress = {};
        (addressRows || []).forEach((row) => {
          nextAddress[row.address_id] = mapRowVerification(row, {
            verified: "address_verified",
            by: "address_verification_by",
            date: "address_verification_date",
            certificate: "address_verification_certificate",
          });
        });
        setAddressVerify(nextAddress);

        const nextSocial = {};
        (socialRows || []).forEach((row) => {
          nextSocial[row.user_social_media_id] = mapRowVerification(row, {
            verified: "social_media_verified",
            by: "social_media_verification_by",
            date: "social_media_verification_date",
            certificate: "social_media_verification_certificate",
          });
        });
        setSocialVerify(nextSocial);

        const nextAwards = {};
        (awardRows || []).forEach((row) => {
          nextAwards[row.awards_certificate_id] = mapRowVerification(row, {
            verified: "awards_verified",
            by: "awards_verification_by",
            date: "awards_verification_date",
            certificate: "awards_verification_certificate",
          });
        });
        setAwardsVerify(nextAwards);
      } catch (error) {
        if (!cancelled) showToast(error.message || "Failed to load user details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  if (!open) return null;

  const fullName = user
    ? [user.user_title, user.first_name, user.last_name].filter(Boolean).join(" ")
    : loginRow?.name || "User";

  const patchUserVerify = (prefix, patch) => {
    setUserVerify((prev) => ({
      ...prev,
      [prefix]: { ...prev[prefix], ...patch },
    }));
  };

  const appendVerification = (fd, keys, state) => {
    const name = state.verified ? state.by || verifierName() : state.by || "";
    fd.append(keys.verified, state.verified ? "1" : "0");
    fd.append(keys.by, state.verified ? name : name);
    fd.append(keys.date, state.date || "");
    if (state.certificateFile) {
      fd.append(keys.certificate, state.certificateFile);
    } else if (state.certificate) {
      fd.append(keys.certificate, String(state.certificate).split("/").pop());
    }
  };

  const handleSave = async () => {
    if (!userId) {
      showToast("This login is not linked to a user master record");
      return;
    }

    setSaving(true);
    try {
      const userFd = new FormData();
      USER_VERIFY_PREFIXES.forEach(({ prefix }) => {
        const state = userVerify[prefix] || {};
        appendVerification(
          userFd,
          {
            verified: `${prefix}_verified`,
            by: `${prefix}_verification_by`,
            date: `${prefix}_verification_date`,
            certificate: `${prefix}_verification_certificate`,
          },
          state
        );
      });
      await userMasterApi.update(userId, userFd);

      await Promise.all(
        addresses.map(async (row) => {
          const state = addressVerify[row.address_id] || {};
          const fd = new FormData();
          appendVerification(
            fd,
            {
              verified: "address_verified",
              by: "address_verification_by",
              date: "address_verification_date",
              certificate: "address_verification_certificate",
            },
            state
          );
          await addressMasterApi.update(row.address_id, fd);
        })
      );

      await Promise.all(
        social.map(async (row) => {
          const state = socialVerify[row.user_social_media_id] || {};
          const fd = new FormData();
          appendVerification(
            fd,
            {
              verified: "social_media_verified",
              by: "social_media_verification_by",
              date: "social_media_verification_date",
              certificate: "social_media_verification_certificate",
            },
            state
          );
          await userSocialMediaProfileApi.update(row.user_social_media_id, fd);
        })
      );

      await Promise.all(
        awards.map(async (row) => {
          const state = awardsVerify[row.awards_certificate_id] || {};
          const fd = new FormData();
          appendVerification(
            fd,
            {
              verified: "awards_verified",
              by: "awards_verification_by",
              date: "awards_verification_date",
              certificate: "awards_verification_certificate",
            },
            state
          );
          await userAwardsCertificateApi.update(row.awards_certificate_id, fd);
        })
      );

      showToast("Verification saved successfully", "success");
      onSaved?.();
      onClose?.();
    } catch (error) {
      showToast(error.message || "Failed to save verification");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="verify-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="verify-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="verify-modal__header">
          <div>
            <h2 id="verify-modal-title">Verify User</h2>
            <p>
              {fullName}
              {loginRow?.user_role ? ` · ${loginRow.user_role}` : ""}
              {loginRow?.phone ? ` · ${loginRow.phone}` : ""}
            </p>
          </div>
          <button type="button" className="verify-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="verify-modal__body">
          {loading && <p className="verify-modal__loading">Loading user details...</p>}

          {!loading && !userId && (
            <p className="verify-modal__empty">
              This login is not linked to a user master profile, so verification is unavailable.
            </p>
          )}

          {!loading && user && (
            <>
              <section className="verify-modal__section">
                <h3>User Master (Read only)</h3>
                <div className="verify-modal__table-wrap">
                  <table className="verify-modal__table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <TableReadRow label="Name" value={fullName} />
                      <TableReadRow label="Gender" value={display(user.gender)} />
                      <TableReadRow label="Age" value={display(user.age)} />
                      <TableReadRow
                        label="Experience (Years)"
                        value={display(user.experience_years)}
                      />
                      <TableReadRow label="Status" value={display(user.status)} />
                      <TableReadRow
                        label="Establishment"
                        value={boolLabel(user.is_establishment)}
                      />
                      <TableReadRow label="Phone" value={display(user.phone_numbers)} />
                      <TableReadRow label="Email" value={display(user.email_addresses)} />
                      <TableReadRow label="Brief Bio" value={display(user.brief_bio)} />
                      <TableReadRow label="Bank Name" value={display(user.bank_name)} />
                      <TableReadRow label="IFSC" value={display(user.ifsc_code)} />
                      <TableReadRow
                        label="Account No"
                        value={display(user.bank_account_number)}
                      />
                      <TableReadRow label="PAN" value={display(user.pan_number)} />
                      <TableReadRow label="Aadhar" value={display(user.aadhar_number)} />
                      <TableReadRow label="GST" value={display(user.gst_number)} />
                      <TableReadRow
                        label="Profile Picture"
                        value={<FileLink path={user.profile_picture} />}
                      />
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="verify-modal__section verify-modal__section--verify">
                <h3>User Master Verification (Editable)</h3>
                {USER_VERIFY_PREFIXES.map(({ prefix, label, verified }) => (
                  <div key={prefix} className="verify-modal__card">
                    <h4>{label}</h4>
                    <VerificationEditor
                      {...(userVerify[prefix] || {})}
                      labels={{
                        verified,
                        by: "Verification By",
                        date: "Verification Date",
                        certificate: "Verification Certificate",
                      }}
                      onChange={(patch) => patchUserVerify(prefix, patch)}
                    />
                  </div>
                ))}
              </section>

              <section className="verify-modal__section">
                <h3>Address Master</h3>
                {addresses.length === 0 && (
                  <p className="verify-modal__empty">No addresses found.</p>
                )}
                {addresses.map((row) => (
                  <div key={row.address_id} className="verify-modal__card">
                    <h4>Address #{row.address_id}</h4>
                    <div className="verify-modal__read-grid">
                      <ReadRow label="Type" value={display(row.address_type_name)} />
                      <ReadRow label="Line 1" value={display(row.address_line_1)} />
                      <ReadRow label="Line 2" value={display(row.address_line_2)} />
                      <ReadRow label="City" value={display(row.city_name)} />
                      <ReadRow label="State" value={display(row.state_location_name)} />
                      <ReadRow label="Country" value={display(row.country_location_name)} />
                      <ReadRow label="Postal Code" value={display(row.postal_code)} />
                    </div>
                    <VerificationEditor
                      {...(addressVerify[row.address_id] || {})}
                      labels={{
                        verified: "Address Verified",
                        by: "Verified By",
                        date: "Verification Date",
                        certificate: "Verification Certificate",
                      }}
                      onChange={(patch) =>
                        setAddressVerify((prev) => ({
                          ...prev,
                          [row.address_id]: { ...prev[row.address_id], ...patch },
                        }))
                      }
                    />
                  </div>
                ))}
              </section>

              <section className="verify-modal__section">
                <h3>Social Media</h3>
                {social.length === 0 && (
                  <p className="verify-modal__empty">No social media profiles found.</p>
                )}
                {social.map((row) => (
                  <div key={row.user_social_media_id} className="verify-modal__card">
                    <h4>{display(row.social_media_platform)}</h4>
                    <div className="verify-modal__read-grid">
                      <ReadRow
                        label="URL"
                        value={
                          row.url ? (
                            <a href={row.url} target="_blank" rel="noreferrer">
                              {row.url}
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                    </div>
                    <VerificationEditor
                      {...(socialVerify[row.user_social_media_id] || {})}
                      labels={{
                        verified: "Social Media Verified",
                        by: "Verified By",
                        date: "Verification Date",
                        certificate: "Verification Certificate",
                      }}
                      onChange={(patch) =>
                        setSocialVerify((prev) => ({
                          ...prev,
                          [row.user_social_media_id]: {
                            ...prev[row.user_social_media_id],
                            ...patch,
                          },
                        }))
                      }
                    />
                  </div>
                ))}
              </section>

              <section className="verify-modal__section">
                <h3>Awards & Certificates</h3>
                {awards.length === 0 && (
                  <p className="verify-modal__empty">No awards found.</p>
                )}
                {awards.map((row) => (
                  <div key={row.awards_certificate_id} className="verify-modal__card">
                    <h4>{display(row.title)}</h4>
                    <div className="verify-modal__read-grid">
                      <ReadRow label="Awarding Body" value={display(row.awarding_body)} />
                      <ReadRow label="Date" value={display(row.award_date)} />
                    </div>
                    <VerificationEditor
                      {...(awardsVerify[row.awards_certificate_id] || {})}
                      labels={{
                        verified: "Awards Verified",
                        by: "Verified By",
                        date: "Verification Date",
                        certificate: "Verification Certificate",
                      }}
                      onChange={(patch) =>
                        setAwardsVerify((prev) => ({
                          ...prev,
                          [row.awards_certificate_id]: {
                            ...prev[row.awards_certificate_id],
                            ...patch,
                          },
                        }))
                      }
                    />
                  </div>
                ))}
              </section>
            </>
          )}
        </div>

        <div className="verify-modal__footer">
          <button type="button" className="master-btn master-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="master-btn master-btn--primary"
            disabled={saving || loading || !userId}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Verification"}
          </button>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
